import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { type AdminRoleRow, type AuditAction, labelFor } from "./types";

/** Loads the admin/super-admin roster and exposes promote/demote/revoke
 *  mutations with audit-log writes. */
export const useAdminsRoster = () => {
  const { user } = useAuth();
  const callerId = user?.id ?? null;
  const [roster, setRoster] = useState<AdminRoleRow[]>([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const adminCount = roster.length;
  const superCount = useMemo(() => roster.filter((r) => r.is_super).length, [roster]);

  const loadRoster = useCallback(async () => {
    setRosterLoading(true);
    const { data: roleRows, error } = await supabase
      .from("user_roles")
      .select("user_id, role, created_at")
      .in("role", ["admin", "super_admin"]);
    if (error) {
      toast.error("Could not load admin roster");
      setRosterLoading(false);
      return;
    }

    const byUser = new Map<string, { is_super: boolean; granted_at: string | null }>();
    for (const r of roleRows ?? []) {
      const existing = byUser.get(r.user_id) ?? { is_super: false, granted_at: null };
      const isSuper = existing.is_super || r.role === "super_admin";
      const granted = !existing.granted_at || (r.created_at && r.created_at < existing.granted_at)
        ? r.created_at
        : existing.granted_at;
      byUser.set(r.user_id, { is_super: isSuper, granted_at: granted });
    }

    const userIds = Array.from(byUser.keys());
    let profiles: Array<{ owner_user_id: string; display_name: string | null; workspace_label: string | null }> = [];
    if (userIds.length > 0) {
      const { data: profileRows } = await supabase
        .from("profiles")
        .select("owner_user_id, display_name, workspace_label")
        .in("owner_user_id", userIds)
        .eq("is_primary", true);
      profiles = profileRows ?? [];
    }
    const profileMap = new Map(profiles.map((p) => [p.owner_user_id, p]));

    const rows: AdminRoleRow[] = userIds
      .map((uid) => {
        const meta = byUser.get(uid)!;
        const p = profileMap.get(uid);
        return {
          user_id: uid,
          is_super: meta.is_super,
          granted_at: meta.granted_at,
          display_name: p?.display_name ?? null,
          workspace_label: p?.workspace_label ?? null,
          email: null,
        };
      })
      .sort((a, b) => {
        if (a.is_super !== b.is_super) return a.is_super ? -1 : 1;
        return labelFor(a).localeCompare(labelFor(b));
      });

    setRoster(rows);
    setRosterLoading(false);
  }, []);

  useEffect(() => {
    void loadRoster();
  }, [loadRoster]);

  const writeAudit = useCallback(async (action: AuditAction, targetUserId: string, label: string, email: string | null) => {
    if (!callerId) return;
    await supabase.from("admin_actions").insert({
      admin_user_id: callerId,
      target_user_id: targetUserId,
      action,
      metadata: { target_label: label, target_email: email },
    });
  }, [callerId]);

  const promoteToSuper = useCallback(async (row: AdminRoleRow) => {
    setBusyUserId(row.user_id);
    const { error } = await supabase.from("user_roles").insert({ user_id: row.user_id, role: "super_admin" });
    if (error) {
      toast.error(`Could not promote: ${error.message}`);
    } else {
      await writeAudit("grant_super_admin_role", row.user_id, labelFor(row), row.email);
      toast.success(`${labelFor(row)} is now a super admin`);
      await loadRoster();
    }
    setBusyUserId(null);
  }, [loadRoster, writeAudit]);

  const demoteFromSuper = useCallback(async (row: AdminRoleRow) => {
    setBusyUserId(row.user_id);
    const { error } = await supabase
      .from("user_roles")
      .delete()
      .eq("user_id", row.user_id)
      .eq("role", "super_admin");
    if (error) {
      toast.error(`Could not demote: ${error.message}`);
    } else {
      await writeAudit("revoke_super_admin_role", row.user_id, labelFor(row), row.email);
      toast.success(`${labelFor(row)} is no longer a super admin`);
      await loadRoster();
    }
    setBusyUserId(null);
  }, [loadRoster, writeAudit]);

  const revokeAdmin = useCallback(async (row: AdminRoleRow) => {
    setBusyUserId(row.user_id);
    if (row.is_super) {
      const { error: superErr } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", row.user_id)
        .eq("role", "super_admin");
      if (superErr) {
        toast.error(`Could not revoke: ${superErr.message}`);
        setBusyUserId(null);
        return;
      }
      await writeAudit("revoke_super_admin_role", row.user_id, labelFor(row), row.email);
    }
    const { error } = await supabase
      .from("user_roles")
      .delete()
      .eq("user_id", row.user_id)
      .eq("role", "admin");
    if (error) {
      toast.error(`Could not revoke: ${error.message}`);
    } else {
      await writeAudit("revoke_admin_role", row.user_id, labelFor(row), row.email);
      toast.success(`${labelFor(row)} no longer has admin access`);
      await loadRoster();
    }
    setBusyUserId(null);
  }, [loadRoster, writeAudit]);

  return {
    callerId,
    roster,
    rosterLoading,
    busyUserId,
    adminCount,
    superCount,
    loadRoster,
    writeAudit,
    promoteToSuper,
    demoteFromSuper,
    revokeAdmin,
  };
};
